import { useEffect, useState, useCallback, useMemo } from "react";
import Schedule from "./Schedule";
import { useServices } from "../services/ServiceContext";
import { Grid2 } from "@mui/material";
import UserList from "./UserList";
import { axiosClient } from "../api/proxy/AxiosClient";

export default function StudentSchedule () {
  const [localSchedule, setLocalSchedule] = useState({ start: null, end: null });
  const { lesson } = useServices();
  const [lessons, setLessons] = useState([]);
  const [teacherLessons, setTeacherLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [newLesson, setNewLesson] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  const fetchLessons = useCallback(async () => {
    try {
      const url = `/lessons?studentUid=${user.uid}`;
      const response = await axiosClient.get(url);
      setLessons(response.data);
    } catch (err) {
      console.error("Error fetching lessons:", err);
    }
  }, [user.uid]);

  const fetchTeacherLessons = useCallback(async () => {
    if (!selectedTeacher) {
      setTeacherLessons([]);
      return;
    }
    try {
      const url = `/lessons?studentUid=${user.uid}&${selectedTeacher ? 'teacherUid=' + selectedTeacher.uid : ""}`;
      const response = await axiosClient.get(url);
      setTeacherLessons(response.data.filter(l => !l.uid));
    } catch (err) {
      console.error("Error fetching lessons:", err);
    }
  }, [user.uid, selectedTeacher]);

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await axiosClient.get(`/students/teachers`);
      setTeachers(response.data);
      setSelectedTeacher(prev => prev ?? response.data?.[0]);
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  }, []);

  const events = useMemo(() => {
    return [...lessons, ...teacherLessons]
      .map(request => ({
        id: request.id,
        uid: request.uid,
        title: `${request.name || '_'}`,
        start: new Date(request.startDttm),
        end: new Date(request.endDttm),
        status: request.status,
        allDay: false,
        lesson: request,
        forSelectedUser: !selectedTeacher || request.teacher.uid === selectedTeacher.uid
      }));
  }, [lessons, teacherLessons, selectedTeacher]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  useEffect(() => {
    fetchTeacherLessons();
  }, [fetchTeacherLessons]);

  useEffect(() => {
    if (newLesson) {
      setLessons((prevLessons) => [...prevLessons, newLesson]);
    }
  }, [newLesson]);

  const handleRangeSelected = (range) => {
    setLocalSchedule(range);
    lesson.openCreateLessonForStudent(selectedTeacher, teachers, range, setNewLesson);
  };

  const setChangedLesson = (updatedLesson) => {
    setLessons((prevLessons) => {
        if (updatedLesson.status === "deleted") {
          return prevLessons.filter((l) => l.id !== updatedLesson.id);
        } else {
          return prevLessons.map((l) => (l.id === updatedLesson.id ? updatedLesson : l));
        }
      }
    );
  }

  const viewEditLesson = (event) => {
    if (!event.uid) return; // Ignore events without uid (e.g., placeholders, time limits, occupied slots){
    lesson.openEditLessonForStudent(event.lesson, lessons, setChangedLesson)
  };

  return (
    <Grid2 container spacing={2} justifyContent="center">
      <UserList
        users={teachers}
        multiselect={false}
        typeIsActive={false}
        selectedUser={selectedTeacher}
        handleSelectedUser={setSelectedTeacher}
      />

      <Schedule
        start={localSchedule.start}
        end={localSchedule.end}
        onRangeSelected={handleRangeSelected}
        events={events}
        selectedUser={selectedTeacher}
        onEventSelected={viewEditLesson}
      />
    </Grid2>
  );
}
